import { debugLog } from '@/shared/utils/debugLog.js';

/**
 * CRITICAL: This guard MUST run AFTER initializationGuard — requires stores to be hydrated
 *
 * Usage in routes:
 * {
 *   path: '/dashboard',
 *   meta: {
 *     middleware: [authenticated]
 *   }
 * }
 *
 * @param {import('vue-router').RouteLocationNormalized} to - Target route
 * @param {import('vue-router').RouteLocationNormalized} from - Source route
 */
export async function middlewareGuard(to, from) {
    const middleware = to.meta?.middleware || [];

    if (!middleware.length) {
        debugLog('MiddlewareGuard', 'No middleware defined for route', { path: to.path });
        return;
    }

    debugLog('MiddlewareGuard', 'Running middleware', {
        path: to.path,
        middlewareCount: middleware.length,
    });

    for (const [index, middlewareFn] of middleware.entries()) {
        const middlewareName = middlewareFn.name || `middleware-${index}`;

        debugLog('MiddlewareGuard', `Executing ${middlewareName}`, {
            index: index + 1,
            total: middleware.length,
        });

        try {
            const result = await middlewareFn(to, from);

            // Middleware returned a value - redirect or abort navigation
            if (result) {
                debugLog('MiddlewareGuard', `${middlewareName} returned redirect`, { result });
                return result;
            }

            debugLog('MiddlewareGuard', `${middlewareName} passed`);
        } catch (error) {
            console.error(`[MiddlewareGuard] ${middlewareName} threw error:`, error);

            // Middleware threw error - abort navigation
            return false;
        }
    }

    debugLog('MiddlewareGuard', 'All middleware passed, continuing navigation');
}
